import { setSliderTimeCenter } from 'actions/timeline'
import { TIME_CODE_CONTAINER_LOCATION, TIME_CODE_STATE } from 'config/constants/timecode'
import { SliderTimeContext } from 'contexts/SliderTimeContext'
import { getFrameStep } from 'helpers/getFrameStep'
import { maxBy } from 'lodash'
import React, { useCallback, useContext, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { selectIsTimelinePlaying } from 'selectors/playback'
import * as Actions from '~/actions'
import { PLAYBACK_STATE } from '~/enums'
import { useAction, useBind, useCompose, useDiff, useStatic } from '~/hooks'
import * as Selectors from '~/selectors'
import { timelineTimeToSeconds, secondsToTimelineTime } from '~/Util'
import { getObjectTimeCode } from '~/Util/timecode'

/**
 * @param {Asset} asset
 * @return {{ mediaStart: number, startTime: number }}
 */
function getActiveAssetTimeParams(asset) {
  if (asset === undefined) {
    return {
      mediaStart: 0,
      startTime: 0,
      duration: 0,
    }
  }

  return {
    mediaStart: asset.mediaStart,
    startTime: asset.startTime,
    duration: asset.duration,
    endTime: asset.endTime,
  }
}

/**
 * @param {Asset} asset
 * @return {[number, boolean]}
 */
export function useAssetProgress(asset) {
  const { startTime, mediaStart } = getActiveAssetTimeParams(asset)
  const latestTimelineRewindTime = useSelector(state => state.timeline.latestTimelineRewindTime)
  const isTimelineRewinded = useSelector(state => state.timeline.isTimelineRewinded)

  const checkIsChanged = (current, prev, isInitial) => isInitial || (current !== prev)
  const isStartTimeChanged = useDiff(startTime, checkIsChanged)
  const isMediaStartChanged = useDiff(mediaStart, checkIsChanged)
  const isTimelineRewindedChanged = useDiff(isTimelineRewinded, checkIsChanged)
  const shouldRewind = (isTimelineRewindedChanged && isTimelineRewinded)
    || isStartTimeChanged || isMediaStartChanged

  return {
    shouldRewind,
    latestTimelineRewindTime,
  }
}

/**
 * @param {object} state
 * @return {NumbersRange}
 */
function getProjectTimeRange(state) {
  const assets = Selectors.getAssets(state)
  if (assets.length === 0) {
    return [ 0, 0 ]
  }
  const { endTime = 0 } = maxBy(assets, asset => asset.endTime) || {}
  return [ 0, endTime ]
}

/**
 * @param {object} state
 * @return {number}
 */
function getProjectProgress(state) {
  const [ projectStartTime ] = getProjectTimeRange(state)
  const { sliderTime } = state.timeline
  return Math.max(0, sliderTime - projectStartTime)
}

/**
 * Calc project duration, INCLUDING blanks between video clips.
 *
 * TODO:
 *  when backend will support rendering blank fragments,
 *  update selectors::getProjectDuration func respectively, and use it here instead of this
 * @see http://18.184.210.86/issues/183#note-17
 *
 * @param {object} state
 * @return {number}
 */
function getProjectDuration(state) {
  const [ startTime, endTime ] = getProjectTimeRange(state)
  return endTime - startTime
}

/**
 * @param {object} state
 * @return {{
 *  duration: number,
 *  progress: number,
 *  startTime: number,
 *  endTime: number
 * }}
 */
export function getProjectParams(state) {
  const [ startTime, endTime ] = getProjectTimeRange(state)
  return {
    duration: getProjectDuration(state),
    progress: getProjectProgress(state),
    startTime,
    endTime,
  }
}

// ---

/**
 * @param {Asset} asset
 * @param {function: ReactPlayerState} getPlayerState
 * @param {object} params
 * @param {boolean} [params.enabled=true]
 * @return {function(object)}
 */
export function usePlayerProgressHandler(asset, getPlayerState) {
  const { setLocalTime, localTimeRef } = useContext(SliderTimeContext)
  const refGetPlayerState = useStatic(getPlayerState)
  const isPlaying = useSelector(selectIsTimelinePlaying)
  const isPlayingRef = useRef(isPlaying)

  useEffect(() => {
    isPlayingRef.current = isPlaying
  }, [ isPlaying ])

  return React.useCallback(
    (data, options) => {
      // react-player emits progress on mount too (always rewinds to 0).
      // We never need that updates.
      const { isBackground } = options ?? {}
      const playerState = refGetPlayerState.current()
      const sec = data.getCurrentTime()
      const { mediaStart, startTime } = asset
      const currentPlayerTime = Math.max(secondsToTimelineTime(sec) - mediaStart, 0) + startTime
      if (!isPlayingRef.current || isBackground
        || playerState.paused
        || !playerState.isReady
        || currentPlayerTime < localTimeRef.current) {
        return
      }
      setLocalTime(currentPlayerTime, options)
    },
    [ asset, setLocalTime, localTimeRef, refGetPlayerState ]
  )
}

/**
 * @return {function(number)}
 */
export function useTimelineRewindHandler() {
  const { startTime: projectStartTime } = useSelector(getProjectParams)
  const { setLocalTime } = useContext(SliderTimeContext)
  const rewind = useCallback(time => {
    setLocalTime(time, { isRewind: true })
  }, [ setLocalTime ])
  return useCompose(
    rewind,
    React.useCallback(
      projectProgress => projectProgress + projectStartTime,
      [ projectStartTime ]
    ),
    sec => secondsToTimelineTime(sec)
  )
}

/**
 * @param {number} fps
 * @return {{ onPrevFrame: function, onNextFrame: function }}
 */
export function useFrameStepHandlers(fps, previewPlayerType) {
  const pause = useAction(Actions.playback.changeTimelinePlaybackState, PLAYBACK_STATE.PAUSE)
  const { setLocalTime, localTimeRef } = useContext(SliderTimeContext)
  const setSliderCenter = useAction(setSliderTimeCenter)
  const activePreview = useSelector(state => state.preview.activePreview)
  const { timeCodeState } = useSelector(state => state.playback)
  const timeMode = useSelector(state => state.playback.timeCodeMode)

  const calcNewSliderTime = React.useCallback(
    forward => {
      const { frameCount, dropFrame } = getObjectTimeCode(
        timelineTimeToSeconds(localTimeRef.current),
        fps, timeMode[TIME_CODE_CONTAINER_LOCATION.TIMELINE]
      )
      const stepSec = getFrameStep({ frameCount, fps, isDF: dropFrame, negative: !forward })
      return secondsToTimelineTime(stepSec)
    },
    [ fps, timeMode, localTimeRef ]
  )

  const onFrameStep = React.useCallback(
    forward => {
      if (timeCodeState !== TIME_CODE_STATE.EDIT
        && (previewPlayerType === activePreview)) {
        const time = calcNewSliderTime(forward)
        pause()
        setLocalTime(time, { isRewind: true })
        setSliderCenter(time)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ pause, setLocalTime, calcNewSliderTime, timeCodeState, previewPlayerType, activePreview ]
  )

  const onNextFrame = useBind(onFrameStep, true)
  const onPrevFrame = useBind(onFrameStep, false)

  return {
    onNextFrame,
    onPrevFrame,
    timeCodeState,
  }
}
